import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiList, apiPatch, apiPost } from '@/lib/api';
import type {
  AuditLogRow,
  Organization,
  PermissionModule,
  Role,
  Sequence,
  Team,
  UserRow,
} from '@/lib/types';

// ── Users ────────────────────────────────────────────────────────────────────

export type UserListParams = {
  page?: number;
  limit?: number;
  q?: string;
  sort?: string;
  status?: string;
  roleId?: string;
  teamId?: string;
};

export function useUsers(params: UserListParams) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => apiList<UserRow[]>('/users', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => apiGet<UserRow>(`/users/${id}`),
    enabled: Boolean(id),
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      email: string;
      roleIds: string[];
      designation?: string;
      department?: string;
    }) => apiPost<{ id: string; email: string; expiresAt: string }>('/users/invitations', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiPatch<UserRow>(`/users/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useAssignRoles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roleIds }: { id: string; roleIds: string[] }) =>
      apiPatch<UserRow>(`/users/${id}/roles`, { roleIds }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      reassignToUserId?: string;
      reason?: string;
    }) => apiPost<UserRow>(`/users/${id}/deactivate`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useReactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<UserRow>(`/users/${id}/reactivate`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useReassignmentCandidates(userId: string | undefined) {
  return useQuery({
    queryKey: ['users', userId, 'candidates'],
    queryFn: () =>
      apiGet<{ id: string; name: string; email: string; designation: string | null }[]>(
        `/users/${userId}/reassignment-candidates`,
      ),
    enabled: Boolean(userId),
  });
}

// ── Roles ────────────────────────────────────────────────────────────────────

export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => apiGet<Role[]>('/roles') });
}

export function usePermissionCatalogue() {
  return useQuery({
    queryKey: ['roles', 'permissions'],
    queryFn: () => apiGet<PermissionModule[]>('/roles/permissions'),
    staleTime: Infinity,
  });
}

export function useSaveRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? apiPatch<Role>(`/roles/${id}`, body) : apiPost<Role>('/roles', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/roles/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });
}

// ── Teams ────────────────────────────────────────────────────────────────────

export function useTeams() {
  return useQuery({ queryKey: ['teams'], queryFn: () => apiGet<Team[]>('/teams') });
}

export function useSaveTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? apiPatch<Team>(`/teams/${id}`, body) : apiPost<Team>('/teams', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['teams'] }),
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/teams/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['teams'] }),
  });
}

// ── Organisation ─────────────────────────────────────────────────────────────

export function useOrganization() {
  return useQuery({
    queryKey: ['organization'],
    queryFn: () => apiGet<Organization>('/organization'),
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPatch<Organization>('/organization', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
}

export function useSequences() {
  return useQuery({
    queryKey: ['organization', 'sequences'],
    queryFn: () => apiGet<Sequence[]>('/organization/sequences'),
  });
}

export function useSaveSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<Sequence>('/organization/sequences', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['organization', 'sequences'] }),
  });
}

// ── Audit log ────────────────────────────────────────────────────────────────

export function useAuditLog(params: {
  page?: number;
  limit?: number;
  q?: string;
  entityType?: string;
  actorId?: string;
}) {
  return useQuery({
    queryKey: ['audit', params],
    queryFn: () => apiList<AuditLogRow[]>('/audit-logs', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

/** Drops empty params so the URL stays readable and caches hit. */
function clean<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== null),
  ) as Partial<T>;
}
